import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Send, Loader2, Bike, Store, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.1 4 9.3 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.3 26.7 36 24 36c-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C9.2 39.6 16 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2C40.9 36.3 44 30.6 44 24c0-1.2-.1-2.3-.4-3.5z"/>
  </svg>
);

const Login = () => {
  const { t } = useTranslation();
  const { login, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const intent = params.get('intent');
  const next = params.get('next');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const intentMeta = intent === 'driver'
    ? { icon: Bike, title: t('auth.intent_driver', 'Sign in to become a driver'),
        body: t('auth.intent_help', "You need a ShipitNow account first. Sign in (or create one) — then we'll take you straight to the next step.") }
    : intent === 'vendor'
      ? { icon: Store, title: t('auth.intent_vendor', 'Sign in to onboard your store'),
          body: t('auth.intent_help', "You need a ShipitNow account first. Sign in (or create one) — then we'll take you straight to the next step.") }
      : null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login({ email, password });
      toast.success('Welcome back!');
      nav(next || '/');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Login failed';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl shadow-slate-200/60 p-8 sm:p-10 fade-in">
        <div className="flex justify-center -mt-12 mb-4">
          <img src="/shipitnow-logo.png" alt="ShipitNow" className="h-14 w-auto" />
        </div>
        <h1 className="text-3xl font-semibold text-slate-900 text-center tracking-tight">{t('auth.welcome_back', 'Welcome to ShipitNow')}</h1>
        <p className="text-center text-slate-500 mt-1">{t('auth.sign_in_to_continue', 'Sign in to continue')}</p>

        {intentMeta && (
          <div className="mt-5 rounded-xl bg-amber-50 border border-amber-200 p-4 flex gap-3" data-testid="intent-banner">
            <intentMeta.icon className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-amber-900 text-sm">{intentMeta.title}</div>
              <p className="text-amber-800/80 text-xs mt-0.5">{intentMeta.body}</p>
              <Link to={`/signup${intent ? `?intent=${intent}&next=${encodeURIComponent(next || '')}` : ''}`}
                className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-amber-900 hover:underline">
                <Info className="h-3.5 w-3.5" /> {t('auth.no_account', 'Need an account?')} {t('nav.sign_up', 'Sign up')}
              </Link>
            </div>
          </div>
        )}

        <button
          onClick={loginWithGoogle}
          className="mt-6 w-full h-11 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors flex items-center justify-center gap-3 text-slate-700 font-medium"
        >
          <GoogleIcon />
          {t('auth.continue_with_google', 'Continue with Google')}
        </button>

        <div className="my-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs text-slate-400 tracking-widest">{t('auth.or', 'OR')}</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-slate-700">{t('auth.email', 'Email')}</Label>
            <div className="relative mt-1.5">
              <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="email" type="email" placeholder="you@example.com" className="pl-9 h-11" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
          </div>
          <div>
            <Label htmlFor="password" className="text-slate-700">{t('auth.password', 'Password')}</Label>
            <div className="relative mt-1.5">
              <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="password" type="password" placeholder="••••••••" className="pl-9 h-11" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
          </div>

          <Button type="submit" disabled={busy} className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t('nav.sign_in', 'Sign in')}
          </Button>
        </form>

        <div className="mt-5 flex items-center justify-between text-sm">
          <button className="text-slate-500 hover:text-slate-900">{t('auth.forgot_password', 'Forgot password?')}</button>
          <p className="text-slate-500">{t('auth.no_account', 'Need an account?')} <Link to="/signup" className="text-slate-900 font-medium hover:underline">{t('nav.sign_up', 'Sign up')}</Link></p>
        </div>

        {!intentMeta && (
          <div className="mt-6 pt-5 border-t border-slate-100">
            <p className="text-[11px] text-slate-400 uppercase tracking-widest font-semibold text-center">
              {t('auth.or_join_as', 'Or join as')}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Link
                to="/login?intent=vendor&next=%2Fvendor-onboarding"
                data-testid="login-cta-vendor"
                className="flex items-center justify-center gap-1.5 h-10 rounded-lg border border-emerald-200 bg-emerald-50/50 hover:bg-emerald-50 text-emerald-800 text-sm font-medium transition-colors"
              >
                <Store className="h-4 w-4" />
                {t('home.become_vendor', 'Become a vendor')}
              </Link>
              <Link
                to="/login?intent=driver&next=%2Fdriver%2Fapply"
                data-testid="login-cta-driver"
                className="flex items-center justify-center gap-1.5 h-10 rounded-lg border border-slate-200 bg-slate-50/60 hover:bg-slate-100 text-slate-800 text-sm font-medium transition-colors"
              >
                <Bike className="h-4 w-4" />
                {t('home.become_driver', 'Become a driver')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
